// src/sidebars/SidebarAdUpload.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
interface SidebarAdUploadProps {
  targetUrl: string;
  onTargetUrlChange: (value: string) => void;

  buttonLabel: string;
  onButtonLabelChange: (value: string) => void;

  onBack: () => void;
  onContinue: () => void;
  isContinueDisabled?: boolean;
  widthPx?: number;
  stickyTopPx?: number;
}

const SidebarAdUpload: React.FC<SidebarAdUploadProps> = ({
  targetUrl,
  onTargetUrlChange,
  buttonLabel,
  onButtonLabelChange,
  onBack,
  onContinue,
  isContinueDisabled = false,
  widthPx = 240,
  stickyTopPx = 64,
}) => {
  const { t } = useTranslation();

  const disabled = !!isContinueDisabled;

  return (
    <div
      style={{
        ...sidebarStyle,
        width: widthPx,
        top: `${stickyTopPx}px`,
      }}
    >
      <h2 style={headingStyle}>
        🛍️ {t('ad.sidebar.heading', 'Advertisement setup')}
      </h2>

      <p style={helperTextStyle}>
        {t(
          'ad.sidebar.description',
          'Choose on which website the user should land when they tap your ad button.'
        )}
      </p>

        {/* URL input */}
        <label style={labelStyle}>
        <span style={labelTextStyle}>
          {t('ad.sidebar.urlLabel', 'Destination URL')}
        </span>
        <input
          type="url"
          value={targetUrl}
          onChange={(e) => onTargetUrlChange(e.target.value)}
          placeholder={t(
            'ad.sidebar.urlPlaceholder',
            'Please paste your URL here, e.g. https://your-shop.com'
          )}
          style={inputStyle}
        />
      </label>

      <p style={hintTextStyle}>
        {t(
          'ad.sidebar.urlHint',
          'Use a short name for the button that draws interest for your product/service. It is also recommended to use the company name like Visit UNYDO'
        )}
      </p>

      {/* Button label input */}
      <label style={labelStyle}>
        <span style={labelTextStyle}>
          {t('ad.sidebar.buttonLabel', 'Button text')}
        </span>
        <input
          type="text"
          value={buttonLabel}
          onChange={(e) => onButtonLabelChange(e.target.value)}
          placeholder={t(
            'ad.sidebar.buttonPlaceholder',
            'e.g. Shop now, Learn more'
          )}
          style={inputStyle}
        />
      </label>

      <div style={{ flex: 1 }} />

      {/* Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Continue (gold) */}
        <button
          onClick={onContinue}
          disabled={disabled}
          style={{
            ...buttonStyleBase,
            background: disabled
              ? 'linear-gradient(135deg,#6b7280,#4b5563)'
              : 'linear-gradient(135deg,#fceabb,#f8b500,#b88700)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.7 : 1,
            color: disabled ? '#e5e7eb' : '#111827',
          }}
        >
          👉 {t('ad.sidebar.buttons.continue', 'Continue')}
        </button>

        {/* Back */}
        <button
          onClick={onBack}
          style={{
            ...buttonStyleBase,
            background: 'rgba(15,23,42,0.9)',
            color: '#e5e7eb',
            border: '1px solid rgba(148,163,184,0.6)',
          }}
        >
          ⬅️ {t('ad.sidebar.buttons.back', 'Back')}
        </button>
      </div>
    </div>
  );
};

const sidebarStyle: React.CSSProperties = {
  height: 'calc(92vh - 78px)',
  background:
    'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(15,23,42,0.99))',
  color: '#e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  position: 'sticky',
  top: '80px', // matches page header offset
  borderRadius: 20,
  border: '1px solid rgba(148,163,184,0.35)',
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  padding: 20,
};

const headingStyle: React.CSSProperties = {
  marginBottom: 4,
  fontSize: 18,
  letterSpacing: -0.02,
};

const helperTextStyle: React.CSSProperties = {
  marginTop: 24,
  marginBottom: 0,
  fontSize: 13,
  color: '#9ca3af',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 0,
};

const labelTextStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#e5e7eb',
};

const inputStyle: React.CSSProperties = {
  width: '95%',
  padding: '8px 12px',
  borderRadius: 10,
  border: '1px solid rgba(148,163,184,0.6)',
  backgroundColor: 'rgba(15,23,42,0.95)',
  color: '#e5e7eb',
  fontSize: 13,
  outline: 'none',
};

const hintTextStyle: React.CSSProperties = {
  marginTop: 36,
  marginBottom: 0,
  fontSize: 12,
  color: '#9ca3af',
};

const buttonStyleBase: React.CSSProperties = {
  padding: '10px 12px',
  width: '100%',
  border: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
};

export default SidebarAdUpload;
